import { CopyOutlined } from '@ant-design/icons'
import { useChannels } from '@entities/channel'
import { CopyIcon } from '@entities/channel/ui/svgs'
import { useEmployees } from '@entities/employee/model/hooks/use-employees'
import type { Employee } from '@entities/employee/model/types'
import useMobileView from '@shared/hooks/isMobile'
import { Checkbox } from '@shared/ui/checkbox'
import type { FormProps, TableProps } from 'antd'
import { App, Button, Card, Col, Divider, Flex, Form, Input, Row, Space, Table, theme, Typography } from 'antd'
import { useMemo } from 'react'

const { Text } = Typography

type FieldType = {
    username: string
    role: string
}

export const EmployeesTab = () => {
    const {
        // actions: { editChannel },
        channelDetails
    } = useChannels()
    const {
        token: { colorWhite, colorTextDescription }
    } = theme.useToken()

    const isMobile = useMobileView()

    const {
        actions: { createEmployee, editEmployee }
    } = useEmployees(channelDetails?.id)

    const [form] = Form.useForm()

    const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
        if (!channelDetails?.id) return

        createEmployee({
            channel: channelDetails?.id,
            permissions: {
                posting: false,
                statistics: false,
                advertising: false
            },
            ...values
        })
    }

    const { message } = App.useApp()

    const columns: TableProps<Employee>['columns'] = useMemo(
        () => [
            {
                title: 'Username сотрудника',
                dataIndex: 'username',
                key: 'username',
                render: (_, record) => record.user.username
            },
            {
                title: 'Роль',
                dataIndex: 'role',
                key: 'role',
                render: (_, record) => record.role
            },
            {
                title: 'Постинг',
                dataIndex: 'posting',
                key: 'posting',
                align: 'center',
                render: (_, record) => (
                    <Checkbox
                        checked={record.permissions.posting}
                        onChange={(val) =>
                            editEmployee({
                                id: record.id,
                                permissions: {
                                    ...record.permissions,
                                    posting: val.target.checked
                                }
                            })
                        }
                    />
                )
            },
            {
                title: 'Реклама',
                dataIndex: 'advertising',
                key: 'advertising',
                align: 'center',
                render: (_, record) => (
                    <Checkbox
                        checked={record.permissions.advertising}
                        onChange={(val) =>
                            editEmployee({
                                id: record.id,
                                permissions: {
                                    ...record.permissions,
                                    advertising: val.target.checked
                                }
                            })
                        }
                    />
                )
            },
            {
                title: 'Статистика',
                dataIndex: 'statistics',
                key: 'statistics',
                align: 'center',
                render: (_, record) => (
                    <Checkbox
                        checked={record.permissions.statistics}
                        onChange={(val) =>
                            editEmployee({
                                id: record.id,
                                permissions: {
                                    ...record.permissions,
                                    statistics: val.target.checked
                                }
                            })
                        }
                    />
                )
            }
        ],
        [editEmployee]
    )

    return (
        <>
            <Form
                form={form}
                name="add-employee"
                onFinish={onFinish}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                style={{ marginTop: `${isMobile ? '0' : '16px'}` }}
            >
                {isMobile ? (
                    <Card size="small" style={{ backgroundColor: colorWhite, border: 'none' }}>
                        <Text style={{ fontSize: 17, marginBottom: 10, color: '#8F8E94', display: 'inline-block' }}>
                            Добавьте сотрудника
                        </Text>
                        <Flex vertical gap={8}>
                            <Form.Item
                                style={{ margin: 0 }}
                                name="username"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Input
                                    style={{
                                        padding: '13px 13px',
                                        borderRadius: 12,
                                        fontSize: 17,
                                        color: '#8F8E94',
                                        border: 'none'
                                    }}
                                    placeholder={'Введите username (@...)'}
                                />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 15 }} name="role" required={false}>
                                <Input
                                    style={{
                                        padding: '13px 13px',
                                        borderRadius: 12,
                                        fontSize: 17,
                                        color: '#8F8E94',
                                        border: 'none'
                                    }}
                                    placeholder="Введите роль сотрудника"
                                />
                            </Form.Item>
                        </Flex>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                style={{ padding: '25px', width: '100%', borderRadius: 12, fontSize: 17 }}
                                htmlType="submit"
                                type={'primary'}
                            >
                                Добавить
                            </Button>
                        </Form.Item>
                        <Divider style={{ margin: '16px 0 16px 0' }} />
                        <Button
                            style={{
                                borderRadius: 12,
                                border: 'none',
                                padding: 25,
                                width: '100%',
                                fontSize: 17,
                                color: '#8F8E94',
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 8
                            }}
                            iconPosition={'end'}
                            icon={<CopyIcon />}
                            onClick={() =>
                                navigator.clipboard
                                    .writeText(
                                        `Я выдаю тебе доступ к своему каналу ${channelDetails?.name} в Telegaplan. Нужно, чтобы ты тоже был пользователем Telegaplan. Для этого перейди по этой ссылке ${window.location.origin}`
                                    )
                                    .then(() => {
                                        message.success('Скопировано в буфер обмена')
                                    })
                            }
                        >
                            Я выдаю тебе доступ к...
                        </Button>
                        <Text>*Если сотрудник еще не пользуется Telegaplan, скопируйте сообщение и отправьте его</Text>
                    </Card>
                ) : (
                    <>
                        <Flex gap={16} align="end">
                            <Form.Item
                                label="Введите username"
                                name="username"
                                required={false}
                                rules={[{ required: true, message: 'Обязательное поле' }]}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item label="Введите роль" name="role" required={false}>
                                <Input placeholder="Копирайтер/Закупщик/Админ" />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit">
                                    Добавить
                                </Button>
                            </Form.Item>
                        </Flex>
                        <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
                            <Col span={24}>
                                <Text style={{ color: colorTextDescription }}>
                                    *Если админ еще не пользуется Telegaplan, скопируйте сообщение справа и отправьте
                                    его.
                                </Text>
                            </Col>
                            <Col span={6}>
                                <Space.Compact>
                                    <Input
                                        value={`Я выдаю тебе доступ к своему каналу ${channelDetails?.name} в Telegaplan. Нужно, чтобы ты тоже был пользователем Telegaplan. Для этого перейди по этой ссылке ${window.location.origin}`}
                                    />
                                    <Button
                                        type="primary"
                                        onClick={() =>
                                            navigator.clipboard
                                                .writeText(
                                                    `Я выдаю тебе доступ к своему каналу ${channelDetails?.name} в Telegaplan. Нужно, чтобы ты тоже был пользователем Telegaplan. Для этого перейди по этой ссылке ${window.location.origin}`
                                                )
                                                .then(() => {
                                                    message.success('Скопировано в буфер обмена')
                                                })
                                        }
                                    >
                                        <CopyOutlined />
                                    </Button>
                                </Space.Compact>
                            </Col>
                        </Row>
                    </>
                )}
            </Form>

            {!isMobile && (
                <Table<Employee>
                    columns={columns}
                    dataSource={channelDetails?.settings?.employees.employees}
                    pagination={false}
                    rowKey={'id'}
                    // loading={isChannelsLoading}
                />
            )}
            {isMobile &&
                channelDetails?.settings?.employees.employees.map((record) => (
                    <Card size="small" style={{ backgroundColor: colorWhite, marginTop: 10 }} key={record.id}>
                        <Flex vertical gap={10}>
                            <Text italic style={{ fontSize: 17 }}>
                                @{record.user.username} - {record.role}
                            </Text>
                            <Flex justify={'space-between'}>
                                <Text style={{ color: '#8F8E94' }}>Постинг</Text>
                                <Checkbox
                                    checked={record.permissions.posting}
                                    onChange={(val) =>
                                        editEmployee({
                                            id: record.id,
                                            permissions: {
                                                ...record.permissions,
                                                posting: val.target.checked
                                            }
                                        })
                                    }
                                />
                            </Flex>
                            <Flex justify={'space-between'}>
                                <Text style={{ color: '#8F8E94' }}>Реклама</Text>
                                <Checkbox
                                    checked={record.permissions.advertising}
                                    onChange={(val) =>
                                        editEmployee({
                                            id: record.id,
                                            permissions: {
                                                ...record.permissions,
                                                advertising: val.target.checked
                                            }
                                        })
                                    }
                                />
                            </Flex>
                            <Flex justify={'space-between'}>
                                <Text style={{ color: '#8F8E94' }}>Статистика</Text>
                                <Checkbox
                                    checked={record.permissions.statistics}
                                    onChange={(val) =>
                                        editEmployee({
                                            id: record.id,
                                            permissions: {
                                                ...record.permissions,
                                                statistics: val.target.checked
                                            }
                                        })
                                    }
                                />
                            </Flex>
                        </Flex>
                    </Card>
                ))}
        </>
    )
}
